import { useCallback, useRef, useState } from "react";
import { useConfirmation } from "context/Mutation/ConfirmationDialogContext";
import { Box, Button, Checkbox, Divider, HStack, Input, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import { File as FileIcon, FilePdf, FileDoc, FileXls, FileCsv, FilePpt, FileText, GlobeSimple, MagnifyingGlass, Plus, UploadSimple, X } from "@phosphor-icons/react";
import { CheckCircle, Copy, Info, Sparkle, Trash, WarningOctagon } from "@phosphor-icons/react";
import { emitAIEvent, removeExtraInstructions } from "api/ai/ai.service";
import fileinfoService, { apiUpload } from "api/common/fileinfo.service";
import { getFileType, getFolderForRound, sizeToString } from "api/common/fileinfo";
import { SmallText } from "components/text/TextTypes";
import DevOnly from "context/Auth/DevOnly";
import { multipartAnswerToString } from "pages/ISSB/IssbDataInput/helper";
import { useQuery } from "react-query";
import { colors } from "theme/foundations/colors";
import { showSuccessToast } from "utils";
import { copyToClipboard, markdownToHtml, stripHtml } from "utils/text-utils";
import { RichTextViewer } from "../../IssbDataInput/components/RichTextEditor/RichTextEditor";
import { CitationModal } from "../../CitationSidePanel";
import { FileList } from "../FileListWithBrowseOrUpload";
import { AIFeedbackComment, AIFeedbackThumbsUpDown } from "./AIFeedbackControls";
import LifecycleState from "api/common/LifecycleState";
import { ACTION_applyAnswer, apiGetThread, apiPostThreadUpdate } from "api/ai/ai.service";
import { truncate } from "lodash";
import { extractAndCountJsonObjects } from "utils/chat-utils";
const FILE_TYPE_ICONS = {
  PDF: FilePdf,
  DOCX: FileDoc,
  DOC: FileDoc,
  XLSX: FileXls,
  XLS: FileXls,
  CSV: FileCsv,
  PPTX: FilePpt,
  PPT: FilePpt,
  TXT: FileText,
  URL: GlobeSimple,
};
function FileTypeIcon({ fileType, size = 20 }) {
  const Icon = FILE_TYPE_ICONS[fileType] || FileIcon;
  return <Icon size={size} color="#718096" />;
}

function RoundFileList({ files: initialFiles, sourceNames, onSendMessage, roundId }) {
  const [files, setFiles] = useState(initialFiles);
  const [excluded, setExcluded] = useState(() => new Set());
  const [showSearch, setShowSearch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const toggleFile = useCallback((fileId) => {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const selectedCount = files.length - excluded.size;

  const handleRegenerate = useCallback(() => {
    if (!onSendMessage) return;
    const selectedNames = files
      .filter(f => !excluded.has(f.id))
      .map(f => f.title || f.filename || f.id);
    const msg = selectedNames.length === initialFiles.length && excluded.size === 0
      ? "Regenerate the answer using all " + files.length + " sources."
      : "Regenerate the answer using these " + selectedNames.length + " sources:\n" + selectedNames.map(n => "- " + n).join("\n");
    onSendMessage(msg);
  }, [onSendMessage, files, initialFiles, excluded]);

  const handleOpenSearch = useCallback(async () => {
    setShowSearch(true);
    setSearchText("");
    setSearchLoading(true);
    try {
      const page = await fileinfoService.list({ size: 1000 });
      const allAvailable = (page?.content || []).filter(f => !files.some(existing => existing.id === f.id));
      setSearchResults(allAvailable);
    } catch (e) {
      console.warn("Failed to load files for add source", e);
      setSearchResults([]);
    }
    setSearchLoading(false);
  }, [files]);

  const handleAddFile = useCallback((file) => {
    setFiles(prev => [...prev, file]);
    setSearchResults(prev => prev.filter(f => f.id !== file.id));
  }, []);

  const handleFileUpload = useCallback(async (fileList) => {
    if (!fileList?.length) return;
    const folder = getFolderForRound(roundId);
    setUploading(true);
    for (const file of Array.from(fileList)) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        if (folder) formData.append("folders", [folder]);
        const newFileInfo = await apiUpload({ data: formData });
        setFiles(prev => [...prev, newFileInfo]);
        setSearchResults(prev => prev.filter(f => f.id !== newFileInfo.id));
      } catch (err) {
        console.error("Error uploading file", err);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = null;
  }, [roundId]);

  const filteredResults = searchText.trim()
    ? searchResults.filter(f => {
        const name = (f.title || f.filename || "").toLowerCase();
        return name.includes(searchText.toLowerCase());
      })
    : searchResults;

  return (
    <Box mt={2}>
      <HStack justify="space-between" px={1} py={1}>
        <Text fontSize="body.xxs" color="gray.500">
          {selectedCount} of {files.length} sources selected
        </Text>
        <HStack spacing={2}>
          <Text
            fontSize="body.xxs"
            color="gray.500"
            cursor="pointer"
            _hover={{ color: "darkTeal" }}
            onClick={() => setExcluded(excluded.size > 0 ? new Set() : new Set(files.map(f => f.id)))}
          >
            {excluded.size > 0 ? "Select all" : "Deselect all"}
          </Text>
        </HStack>
      </HStack>
      <Box borderTop="1px solid" borderColor="gray.100">
        <VStack spacing={0} align="stretch" maxH="240px" overflowY="auto">
          {files.map((file, i) => (
            <RoundFileRow
              key={file.id || i}
              file={file}
              isExcluded={excluded.has(file.id)}
              onToggle={() => toggleFile(file.id)}
            />
          ))}
        </VStack>
      </Box>

      {/* Add source */}
      {showSearch ? (
        <Box mt={2} border="1px solid" borderColor="gray.200" borderRadius="md" p={2}>
          <HStack mb={2}>
            <HStack flex={1} bg="gray.50" borderRadius="md" px={2} py={1}>
              <MagnifyingGlass size={14} color="gray" />
              <Input
                placeholder="Search sources..."
                size="sm"
                variant="unstyled"
                fontSize="body.xs"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                autoFocus
              />
            </HStack>
            <Button variant="ghost" size="xs" onClick={() => setShowSearch(false)}>
              <X size={14} />
            </Button>
          </HStack>
          <VStack spacing={0} align="stretch" maxH="160px" overflowY="auto">
            {searchLoading && <Text fontSize="body.xxs" color="gray.400" p={2}>Loading...</Text>}
            {!searchLoading && filteredResults.length === 0 && (
              <Text fontSize="body.xxs" color="gray.400" p={2}>No additional sources found</Text>
            )}
            {!searchLoading && filteredResults.map((file) => {
              const fileType = getFileType(file);
              const name = file.title || file.filename || file.id;
              return (
                <HStack
                  key={file.id}
                  py={1.5}
                  px={1}
                  spacing={2}
                  borderBottom="1px solid"
                  borderColor="gray.50"
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => handleAddFile(file)}
                >
                  <Box flexShrink={0}>
                    <FileTypeIcon fileType={fileType} size={20} />
                  </Box>
                  <Text fontSize="body.xs" color="darkTeal" noOfLines={1} flex={1} title={name}>
                    {name}
                  </Text>
                  <Plus size={14} color="gray" />
                </HStack>
              );
            })}
          </VStack>
        </Box>
      ) : (
        <HStack pt={2} px={1} spacing={2}>
          <Button
            variant="outline"
            size="sm"
            borderRadius="full"
            flex={1}
            borderColor="gray.300"
            color="gray.600"
            fontWeight="normal"
            leftIcon={<MagnifyingGlass size={14} />}
            _hover={{ borderColor: "gray.400", color: "darkTeal" }}
            onClick={handleOpenSearch}
          >
            Browse existing
          </Button>
          <Button
            variant="outline"
            size="sm"
            borderRadius="full"
            flex={1}
            borderColor="gray.300"
            color="gray.600"
            fontWeight="normal"
            leftIcon={uploading ? <Spinner size="xs" /> : <UploadSimple size={14} />}
            _hover={{ borderColor: "gray.400", color: "darkTeal" }}
            onClick={() => fileInputRef.current?.click()}
            isDisabled={uploading}
          >
            {uploading ? "Uploading..." : "Upload new"}
          </Button>
          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => handleFileUpload(e.target.files)}
          />
        </HStack>
      )}

      {onSendMessage && (
        <Box pt={2} px={1}>
          <Button
            variant="primary"
            size="sm"
            borderRadius="full"
            w="100%"
            onClick={handleRegenerate}
            isDisabled={selectedCount === 0}
          >
            Regenerate with {selectedCount} source{selectedCount !== 1 ? "s" : ""}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function RoundFileRow({ file, isExcluded, onToggle }) {
  const fileType = getFileType(file);
  const name = file.title || file.filename || file.id;
  return (
    <HStack
      py={1.5}
      px={1}
      spacing={2}
      borderBottom="1px solid"
      borderColor="gray.100"
      opacity={isExcluded ? 0.5 : 1}
      cursor="pointer"
      _hover={{ bg: "gray.50" }}
      onClick={onToggle}
    >
      <Checkbox
        isChecked={!isExcluded}
        onChange={onToggle}
        size="sm"
        colorScheme="green"
        onClick={(e) => e.stopPropagation()}
      />
      <Box flexShrink={0}>
        <FileTypeIcon fileType={fileType} size={20} />
      </Box>
      <Text
        fontSize="body.xs"
        color={isExcluded ? "gray.400" : "darkTeal"}
        textDecoration={isExcluded ? "line-through" : "none"}
        noOfLines={1}
        flex={1}
        title={name}
      >
        {name}
      </Text>
      {file.size > 0 && (
        <Text fontSize="body.xxs" color="gray.400" flexShrink={0}>
          {sizeToString(file.size)}
        </Text>
      )}
    </HStack>
  );
}

function StyledSourceList({ files }) {
  return (
    <Box borderTop="1px solid" borderColor="gray.100" mt={1}>
      <VStack spacing={0} align="stretch">
        {files.map((file, i) => {
          const fileType = getFileType(file);
          const name = file.title || file.filename || file.id;
          return (
            <HStack
              key={file.id || i}
              py={1.5}
              px={1}
              spacing={2}
              borderBottom="1px solid"
              borderColor="gray.100"
            >
              <Box flexShrink={0}>
                <FileTypeIcon fileType={fileType} size={20} />
              </Box>
              <Text
                fontSize="body.xs"
                color="darkTeal"
                noOfLines={1}
                flex={1}
                title={name}
              >
                {name}
              </Text>
              {file.pages && (
                <Text fontSize="body.xxs" color="gray.500" flexShrink={0}>
                  p. {file.pages}
                </Text>
              )}
              {file.size > 0 && (
                <Text fontSize="body.xxs" color="gray.400" flexShrink={0}>
                  {sizeToString(file.size)}
                </Text>
              )}
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
}

/**
 *
 *
 * @param {Object} props
 * @param {!ChatMessage} props.message
 * @param {boolean} props.isLastMessage
 * @param {?FileInfo[]} props.fileinfos
 * @returns
 */
export default function AIChatMessage({ threadId, message, setMessages, allMessages, isLastMessage, debug, onSendMessage }) {
  const { toast } = useToast();
  const [citationModal, setCitationModal] = useState(null);

  let isAI = "assistant tool critic system".includes(message.role);
  let messageType = "ChatMessage"; // default placeholder in case of unknown
  let msg = message?.content;

  if (message?.json_content) {
    // Prefer structured content if available
    messageType = message.json_content?.type;

    // Handle message content with proper precedence
    if (message.json_content.message) {
      msg = message.json_content.message; // Note: can be "" / unset
    } else if (message.json_content.answer) {
      msg =
        typeof message.json_content.answer === "string"
          ? message.json_content.answer
          : multipartAnswerToString(message.json_content.answer);
    } else if (message.json_content.type === "function" && message.json_content.function) {
	  const fn = message.json_content.function;
      msg = "Tool call: " +fn.name + " - " + truncate(JSON.stringify(fn.arguments), {length: 512});
    }
  } else if (isAI) {
    // Fallback to parsing raw content
	if ( ! message.error) {
		try {
			const parsedContent = JSON.parse(message.content);
			messageType = parsedContent.type;
			msg = parsedContent.message || parsedContent.answer;
		} catch (e) {
			// Raw content is not JSON, use as-is (can happen with AI chat forgetting to use the right format)
			console.log("(swallow) AI message content is not valid JSON.", message?.content);
		}	
	}
  }
  if (message.role === "tool") {
	msg = "Tool results: " + truncate(message.content, {length: 512});
  }
  
  // If there is no message at all, show something
  if (!msg || msg === "null") {
    console.warn("No message content!", message);
    msg = `Type: ${messageType}. <br/> No chat response available.`;
  }

  // HACK: remove any extra instructions from the message
  msg = debug ? msg : removeExtraInstructions(msg);

  // HACK detect error message (odd output seen Oct 2, 2025)
  if (message.error || message.rate_limit_error || msg?.includes("ThrottlingException")) {
	if ( ! isAI) {
		console.error("Error message but not AI?!", {...message});
	}
	isAI = true;
  }

  // HACK: the LLM can include a data-request or tool-call in the message.
  // e.g. "To properly address this question, we would need more targeted information.
  // { "type": "data_request", "request": "Please provide any available data on Morgan Stanley's refrigerant consumption" }
  if (msg?.includes("data_request") || msg?.includes('"type": "function"')) {
    // remove json from message
    msg = msg.replace(/```json[\s\S]*?```/, "");
    msg = msg.replace(/{[\s\S]*?}/, "").trim();
    if (message.json_content?.type === "data_request") {
      msg = "\n\nData request: " + message.json_content.request;
    }
    if (!msg) {
      msg = "To properly address this question we need more information.";
    }
  }

  const doCopyToClipboard = async () => {
    copyToClipboard(msg);
  };


  const handleApplyAnswer = () => {
    // Convert any markdown in the answer to HTML.
    let messageToPost = message;
    if (messageToPost.json_content?.message) {
      messageToPost.json_content.message = markdownToHtml(messageToPost.json_content.message);
    } else if (messageToPost.json_content?.answer) {
      messageToPost.json_content.answer =
        typeof messageToPost.json_content.answer === "string"
          ? markdownToHtml(messageToPost.json_content.answer)
          : messageToPost.json_content.answer;
    }
    // wiring - rely on AI context
    emitAIEvent({ action: ACTION_applyAnswer, message: messageToPost });
  };

  // Note: state var defined here for sharing between two widgets -- actual use is in AIFeedbackControls.jsx
  const [feedback, setFeedback] = useState();
  const [showFeedbackComment, setShowFeedbackComment] = useState(); // start unset - the comment widget will initialise

  // FIXME When the source is round questions, we don't get files just json_content.sources
  // HACK files used
  const fileIds = message.json_content?.files;
  const { data: fileinfosPage, isLoading: isLoadingFileInfos } = useQuery(
    ["fileinfoService.list", "" + fileIds],
    () => fileinfoService.list({ ids: fileIds }),
    { enabled: !!fileIds?.length }
  );
  const fileinfos = fileinfosPage?.content;
  const sourceSlices = message.json_content?.sourceSlices;
  fileinfos?.forEach((fileinfo) => {
    const slices = sourceSlices?.filter((slice) => "" + slice.fileinfoId === "" + fileinfo.id); // Note: paranoia on long/string ids
    if (slices?.length) {
      const pages = slices.map((slice) => slice.page);
      fileinfo.pages = pages.join(", "); // HACK to show page numbers
    }
  });

  // e.g. type = answer, dashboard_widget
  const type = message.json_content?.type;

  const { confirm } = useConfirmation();
  const archiveMessage = async (message, threadId) => {
    console.warn("archiveMessage", message, threadId);
    const confirmed = await confirm({
      title: "Delete message",
      message: "Are you sure you want to delete (archive) this message?",
      confirmText: "Delete",
      cancelText: "Cancel",
    });
    if (!confirmed) return;
    // load the thread (ensure we're up to date)
    const thread = await apiGetThread(threadId);
    // get the fresh message and mark as archived on both copies (for the UI and the save)
    message.state = LifecycleState.ARCHIVED;
      //Use the thread array and message index to delete it.
      const freshUserMessageIndex = allMessages.indexOf(message);
      const freshUserMessage = thread.messages[freshUserMessageIndex];
      freshUserMessage.state = LifecycleState.ARCHIVED;
      // console.warn("user message state = archived", message);
    // save the thread
    await apiPostThreadUpdate(thread)
      .then((data) => {
        setMessages(data.messages);
      })
      .catch((err) => {
        // console.log("status", err.response?.status);
        // console.log("allow", err.response?.headers?.allow);
        // console.log("data", err.response?.data);
        throw err;
      });
    showSuccessToast(toast, "Message archived");
  }; // ./archiveMessage

  const checks = message.checks || {};
  const failedChecks = Object.entries(checks).filter(([_, check]) => check?.state === "fail");
  const checksMessage = "Checks run on this response:\n"
  + Object.entries(checks).filter(([_, check]) => check).map(([key, check]) => `${key}: ${check?.message || check?.state || JSON.stringify(check)}`).join("\n");

  return (
    <HStack position="relative">
      {!isAI && <Box flexGrow={1} />}
      <Box
        bg={isAI ? "white" : colors.darkTeal}
        alignSelf={message.role === "user" ? "flex-end" : "flex-start"}
        mt={3}
        p={2}
        maxW="90%"
        borderRadius="md"
        dropShadow="md"
		opacity={message.state === LifecycleState.ERROR || message.state === LifecycleState.ARCHIVED ? 0.6 : 1}
      >
        {/*
		  - RichTextViewer is Global use across multiple places for consistency.
		  - It supports a limited subset of HTML tags and styles.
		  - markdownToHtml is used to convert if AI produce markdown to HTML. So the output will always be HTML.
		  */}
        <Box display="flex" flexDirection="row">
          <RichTextViewer minH={0} p={0} color={isAI ? "darkTeal" : "white"} content={markdownToHtml(msg)}
            onElementClick={(tagName, domElement, attributes) => {
              if (tagName === "CITE") setCitationModal({ citation: attributes, domElement });
            }}
          />
          {!isAI /* delete for user messages */ && (
            <DevOnly style={{ marginLeft: 10 }}>
              <Button variant="ghost" fontSize="body.xxs" size="xxs" onClick={() => archiveMessage(message, threadId)}>
                <Trash size={18} color={colors.esg_white} />
              </Button>
            </DevOnly>
          )}
        </Box>
        {fileinfos?.length > 0 && (
          <Box mt={2}>
            <SmallText>Sources</SmallText>
            <StyledSourceList files={fileinfos} />
          </Box>
        )}
        {message.roundFiles?.length > 0 && (
          <RoundFileList files={message.roundFiles} sourceNames={message.sourceNames} onSendMessage={onSendMessage} roundId={message.roundId} />
        )}
        {isAI && (
          <>
		  {debug && message.json_content && (
		    <Box
		      mt={2}
		      p={2}
		      bg="gray.50"
		      borderRadius="md"
		      border="1px solid"
		      borderColor="gray.200"
		      fontSize="body.xxs"
		      fontFamily="mono"
		    >
		      <HStack spacing={2} mb={1}>
		        <Text color="gray.500" fontWeight="semibold">Type</Text>
		        <Text
		          bg="gray.200"
		          px={1.5}
		          py={0.5}
		          borderRadius="sm"
		          color="gray.700"
		        >
		          {type}
		        </Text>
		      </HStack>
		      {message.json_content.sources && (
		        <Text color="gray.600" mb={0.5}>
		          <Text as="span" fontWeight="semibold" color="gray.500">Slices in context:</Text>{" "}
		          {message.json_content.sources.length}
		        </Text>
		      )}
		      {message.json_content.sourceSlices && (
		        <Text color="gray.600" mb={0.5}>
		          <Text as="span" fontWeight="semibold" color="gray.500">Slices used:</Text>{" "}
		          {message.json_content.sourceSlices.map(
		            (slice) => slice.title || slice.question_number
		          ).join(", ")}
		        </Text>
		      )}
		      <Text color="gray.500">
		        <Text as="span" fontWeight="semibold">Fields:</Text>{" "}
		        {Object.keys(message.json_content)
		          .filter((key) => ("" + message.json_content[key]) !== "" && !["type", "sources", "sourceSlices", "function"].includes(key))
		          .join(", ")}
		      </Text>
		    </Box>
		  )}
		  {debug && extractAndCountJsonObjects(message.content) > 1 && (
		    <Text mt={1} fontSize="body.xxs" color="orange.500" fontWeight="medium">
		      Formatting error in AI output (multiple JSON objects)
		    </Text>
		  )}
		  {debug && (message.issue || message.error) && (
		    <Text mt={1} fontSize="body.xxs" color="red.500" fontWeight="medium">
		      Issue: {message.issue || message.error}
		    </Text>
		  )}
            <HStack justifyContent="space-between" mt={1}>
              <Sparkle size={24} color={colors.navIconLime} />
              <HStack>
                {type === "answer" && (
                  <Button
                    fontSize="body.xs"
                    onClick={handleApplyAnswer}
                    variant={isLastMessage ? "primary" : "secondary"}
                    size="sm"
                    borderRadius="full"
                    /* Note: if the user doesnt have canSave it'd be nice to alter "Apply" to "Save Draft" but is that worth the faff for now? */
                  >
                    Apply answer
                  </Button>
                )}
                <AIFeedbackThumbsUpDown
                  threadId={threadId}
                  message={message}
                  feedback={feedback}
                  setFeedback={setFeedback}
                  setShowFeedbackComment={setShowFeedbackComment}
                />
                <Button variant="ghost" size="sm" onClick={doCopyToClipboard}>
                  <Copy size={24} color={colors.navIconLime} />
                </Button>

				{message.checks && Object.keys(message.checks).length > 0 && 
				<DevOnly>
				 <Button /* debug button */				  
                    variant="ghost"
                    fontSize="body.xxs"
                    size="xs"
					title="Show check info"
                    onClick={() => {console.log("clicked-message: checks", message.checks); alert(checksMessage)}}
                  >
						<CheckCircle size={24} color={failedChecks?.length > 0? colors.esg_red : colors.navIconLime} />
                  </Button>
                </DevOnly>}

                <DevOnly>
                  <Button /* debug button */				  
                    variant="ghost"
                    fontSize="body.xxs"
                    size="xs"
					title="Show debug message + log to console"
                    onClick={() => {console.log("clicked-message", message); alert(JSON.stringify(message)); }}
                  >
                    <Info size={24} color={colors.navIconLime} />
                  </Button>
                </DevOnly>
                {message?.json_content?.error && (
                  <Button
                    variant="ghost"
                    fontSize="body.xxs"
                    size="xs"
                    onClick={() => alert(message.json_content.error)}
                  >
                    <WarningOctagon size={24} color={colors.esg_red} />
                  </Button>
                )}
                <DevOnly>
                  <Button
                    variant="ghost"
                    fontSize="body.xxs"
                    size="xs"
                    onClick={() => archiveMessage(message, threadId)}
                  >
                    <Trash size={24} color={colors.navIconLime} />
                  </Button>
                  {message?.state && message.state !== "ACTIVE" && <Text>{message.state}</Text>}
                </DevOnly>
              </HStack>
            </HStack>
            <AIFeedbackComment
              threadId={threadId}
              message={message}
              feedback={feedback}
              setFeedback={setFeedback}
              showFeedbackComment={showFeedbackComment}
              setShowFeedbackComment={setShowFeedbackComment}
            />
          </>
        ) /* ./isAi */}
		{!isAI && debug && message.state && (
		  <Text fontSize="body.xxs" fontFamily="mono" color="whiteAlpha.700" mt={1}>
		    State: {message.state}
		  </Text>
		)}
      </Box>
      {isAI && <Box flexGrow={1} /> /* doc: why?? */}
      {citationModal && (
        <CitationModal
          isOpen={!!citationModal}
          onClose={() => setCitationModal(null)}
          citation={citationModal.citation}
          domElement={citationModal.domElement}
        />
      )}
    </HStack>
  );
} // ./Message
